import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {
    Accordion, AccordionDetails, AccordionSummary, Alert, Autocomplete, Box, Button, Chip, CircularProgress, Container, FormControlLabel, Paper, Stack, Switch, Tab, Tabs, TextField, ToggleButton, ToggleButtonGroup, Typography,
} from '@mui/material';
import ApiService from '../network/API';
import {useSearchParams} from 'react-router-dom';
import {confirmDialog} from '../confirm';

const errText = (e, fallback) => (typeof e === 'string' ? e : e?.reason || e?.message) || fallback;

const fmtDate = (iso) => {
    const [y, m, d] = iso.split('-').map(Number);
    return new Date(y, m - 1, d).toLocaleDateString('ru-RU', {weekday: 'short', day: 'numeric', month: 'long'});
};

const KIND = {
    MISSING: {label: 'Нет в журнале', color: 'error'},
    EXTRA: {label: 'Нет в расписании', color: 'warning'},
};

// ---------- Связка ----------

const sheetId = (v) => {
    const m = /\/spreadsheets\/d\/([A-Za-z0-9_-]+)/.exec(v || '');
    return (m ? m[1] : (v || '')).trim();
};

// Лист Google-таблицы с посещаемостью этого предмета.
const SheetPicker = ({link, defaultId, onSaved}) => {
    const [id, setId] = useState(link.sheetSpreadsheetId || defaultId || '');
    const [title, setTitle] = useState(link.sheetTitle || null);
    const [sheets, setSheets] = useState(link.sheetTitle ? [link.sheetTitle] : []);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);
    const changed = sheetId(id) !== (link.sheetSpreadsheetId || '') || (title || null) !== (link.sheetTitle || null);

    const loadSheets = () => {
        const sid = sheetId(id);
        if (!sid) return;
        setLoading(true);
        setError(null);
        ApiService.getGoogleSpreadsheet(sid)
            .then((r) => setSheets(r.sheets.map((x) => x.title)))
            .catch((e) => setError(errText(e, 'Не удалось открыть таблицу')))
            .finally(() => setLoading(false));
    };

    const save = () => {
        setSaving(true);
        setError(null);
        ApiService.linkAsursoSheet(link.tagId, {spreadsheetId: sheetId(id), sheetTitle: title || ''})
            .then(onSaved)
            .catch((e) => setError(errText(e, 'Не удалось сохранить')))
            .finally(() => setSaving(false));
    };

    return (
        <Box sx={{display: 'flex', gap: 1.5, alignItems: 'center', flexWrap: 'wrap', width: '100%', mt: 0.5}}>
            <TextField size="small" sx={{flex: '2 1 260px'}} label="Google-таблица посещаемости (ссылка или id)" value={id}
                       onChange={(e) => setId(e.target.value)} onBlur={loadSheets}/>
            <Autocomplete size="small" sx={{flex: '2 1 240px'}} options={sheets} value={title} onChange={(_, v) => setTitle(v)}
                          onOpen={() => { if (sheets.length <= 1) loadSheets(); }} loading={loading}
                          renderInput={(p) => <TextField {...p} label="Лист"/>}/>
            <Button size="small" variant="outlined" disabled={!changed || saving || (sheetId(id) && !title)} onClick={save}>Сохранить лист</Button>
            {error && <Typography variant="caption" color="error">{error}</Typography>}
        </Box>
    );
};

const SubjectRow = ({group, link, onSaved, defaultSheetId}) => {
    const [value, setValue] = useState(link.journalSubject || null);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);
    const names = useMemo(() => group.journalSubjects.map((s) => s.name), [group.journalSubjects]);
    const changed = (value || null) !== (link.journalSubject || null);
    const mine = useMemo(() => new Set(group.journalSubjects.filter((s) => s.mine).map((s) => s.name)), [group.journalSubjects]);

    const save = () => {
        setSaving(true);
        setError(null);
        ApiService.linkAsursoSubject(link.tagId, value || '')
            .then(onSaved)
            .catch((e) => setError(errText(e, 'Не удалось сохранить')))
            .finally(() => setSaving(false));
    };

    return (
        <Box sx={{display: 'flex', gap: 1.5, alignItems: 'center', flexWrap: 'wrap', py: 1, borderTop: 1, borderColor: 'divider'}}>
            <Box sx={{flex: '1 1 220px', minWidth: 0}}>
                <Typography sx={{fontWeight: 500}}>{link.tagName}</Typography>
                <Typography variant="caption" color="text.secondary">
                    {link.ourTotalHours != null ? `план у нас: ${link.ourTotalHours} ч` : 'план часов у нас не задан'}
                    {link.found && link.journalPlannedHours != null ? ` · в журнале: ${link.journalPlannedHours} ч` : ''}
                    {link.found && link.ourTotalHours != null && link.journalPlannedHours != null && link.ourTotalHours !== link.journalPlannedHours ? ' · часы не совпадают' : ''}
                </Typography>
            </Box>
            <Autocomplete
                size="small" sx={{flex: '2 1 280px'}} options={names} value={value} onChange={(_, v) => setValue(v)}
                disabled={group.journalGroupId == null || names.length === 0}
                groupBy={(o) => (mine.has(o) ? 'Мои предметы в журнале' : 'Остальные')}
                renderInput={(p) => <TextField {...p} label="Предмет в журнале" placeholder="не связан"/>}
            />
            <Button size="small" variant="contained" disabled={!changed || saving} onClick={save}>Сохранить</Button>
            {link.journalSubject && !link.found && group.journalGroupId != null && !error && (
                <Chip size="small" color="warning" label="Такого предмета в журнале нет"/>
            )}
            {error && <Typography variant="caption" color="error">{error}</Typography>}
            {link.found && <SheetPicker link={link} defaultId={defaultSheetId} onSaved={onSaved}/>}
        </Box>
    );
};

const LinksTab = () => {
    const [groups, setGroups] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const load = useCallback((refresh) => {
        setLoading(true);
        setError(null);
        ApiService.getAsursoLinks(refresh)
            .then(setGroups)
            .catch((e) => setError(errText(e, 'Не удалось загрузить связку')))
            .finally(() => setLoading(false));
    }, []);

    useEffect(() => { load(false); }, [load]);

    // Подсказка: id таблицы, уже выбранной у какого-либо предмета.
    const defaultSheetId = (groups || []).flatMap((g) => g.subjects).map((l) => l.sheetSpreadsheetId).find(Boolean) || '';

    return (
        <Stack spacing={2}>
            <Stack direction="row" spacing={1} alignItems="center">
                <Typography variant="body2" color="text.secondary" sx={{flex: 1}}>
                    Группы связываются с журналом автоматически по названию. Для каждого предмета выберите соответствующий предмет журнала —
                    по нему идёт сверка. Журнал только читается, в нём ничего не меняется.
                </Typography>
                <Button onClick={() => load(true)} disabled={loading}>Обновить из журнала</Button>
            </Stack>
            {error && <Alert severity="error">{error}</Alert>}
            {loading && !groups && <Box sx={{textAlign: 'center', py: 4}}><CircularProgress/></Box>}
            {groups && groups.filter((g) => g.subjects.length > 0).map((g) => (
                <Paper key={g.groupId} variant="outlined" sx={{p: 2}}>
                    <Stack direction="row" spacing={1} alignItems="center" sx={{mb: 0.5, flexWrap: 'wrap'}}>
                        <Typography variant="h6">{g.groupName}</Typography>
                        {g.journalGroupId != null
                            ? <Chip size="small" color="success" variant="outlined" label={`в журнале${g.period ? ` · ${g.period}` : ''}`}/>
                            : <Chip size="small" color="warning" label="группы нет в журнале"/>}
                    </Stack>
                    {g.error && <Alert severity="warning" sx={{mb: 1}}>{g.error}</Alert>}
                    {g.subjects.map((l) => <SubjectRow key={l.tagId} group={g} link={l} defaultSheetId={defaultSheetId} onSaved={() => load(false)}/>)}
                </Paper>
            ))}
        </Stack>
    );
};

// ---------- Сверка ----------

const reportText = (results) => {
    const bad = results.filter((r) => !r.ok && !r.error);
    if (bad.length === 0) return '';
    const lines = ['Расхождения между журналом АСУ РСО и расписанием:', ''];
    bad.forEach((r) => {
        lines.push(`${r.groupName} — ${r.tagName}${r.journalSubject ? ` (${r.journalSubject})` : ''}`);
        r.items.forEach((i) => lines.push(`  • ${fmtDate(i.date)}: ${i.message}${i.note ? ` (${i.note})` : ''}`));
        lines.push('');
    });
    return lines.join('\n').trim();
};

// Одно расхождение с заметкой администратора (сохраняется у нас, в журнале ничего не меняется).
const ItemRow = ({r, i, onSaved}) => {
    const [editing, setEditing] = useState(false);
    const [text, setText] = useState(i.note || '');
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);

    const save = () => {
        setSaving(true);
        setError(null);
        ApiService.saveAsursoNote({groupId: r.groupId, tagId: r.tagId, date: i.date, kind: i.kind, text})
            .then(() => { setEditing(false); onSaved(i, text.trim()); })
            .catch((e) => setError(errText(e, 'Не удалось сохранить')))
            .finally(() => setSaving(false));
    };

    return (
        <Box sx={{py: 0.75, borderTop: 1, borderColor: 'divider', mt: 1}}>
            <Box sx={{display: 'flex', gap: 1.5, alignItems: 'center', flexWrap: 'wrap'}}>
                <Typography sx={{minWidth: 150}}>{fmtDate(i.date)}</Typography>
                <Chip size="small" color={KIND[i.kind]?.color || 'default'} label={KIND[i.kind]?.label || i.kind}/>
                <Box sx={{display: 'flex', gap: 1, alignItems: 'center'}}>
                    <Box sx={{px: 1.25, py: 0.25, borderRadius: 1, border: 1, borderColor: 'divider', textAlign: 'center'}}>
                        <Typography variant="caption" color="text.secondary" sx={{display: 'block', lineHeight: 1.2}}>журнал</Typography>
                        <Typography sx={{fontWeight: 700, fontSize: 18, lineHeight: 1.2}} color={i.journalCount < i.scheduleCount ? 'error.main' : 'text.primary'}>{i.journalCount}</Typography>
                    </Box>
                    <Typography color="text.secondary">≠</Typography>
                    <Box sx={{px: 1.25, py: 0.25, borderRadius: 1, border: 1, borderColor: 'divider', textAlign: 'center'}}>
                        <Typography variant="caption" color="text.secondary" sx={{display: 'block', lineHeight: 1.2}}>расписание</Typography>
                        <Typography sx={{fontWeight: 700, fontSize: 18, lineHeight: 1.2}} color={i.scheduleCount < i.journalCount ? 'error.main' : 'text.primary'}>{i.scheduleCount}</Typography>
                    </Box>
                </Box>
                <Typography variant="body2" color="text.secondary" sx={{flex: '1 1 200px'}}>{i.message}</Typography>
                {!editing && <Button size="small" onClick={() => { setText(i.note || ''); setEditing(true); }}>{i.note ? 'Изменить' : 'Заметка'}</Button>}
            </Box>
            {!editing && i.note && (
                <Typography variant="body2" sx={{mt: 0.5, pl: 1.5, borderLeft: 3, borderColor: 'primary.main', whiteSpace: 'pre-wrap'}}>{i.note}</Typography>
            )}
            {editing && (
                <Stack spacing={1} sx={{mt: 1}}>
                    <TextField size="small" multiline minRows={2} fullWidth autoFocus value={text} onChange={(e) => setText(e.target.value)}
                               placeholder="Например: сообщил в учебную часть, ответили..." inputProps={{maxLength: 2000}}/>
                    {error && <Typography variant="caption" color="error">{error}</Typography>}
                    <Stack direction="row" spacing={1}>
                        <Button size="small" variant="contained" disabled={saving} onClick={save}>Сохранить</Button>
                        <Button size="small" disabled={saving} onClick={() => setEditing(false)}>Отмена</Button>
                    </Stack>
                </Stack>
            )}
        </Box>
    );
};

const CompareTab = () => {
    const [results, setResults] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [copied, setCopied] = useState(false);

    const run = useCallback((refresh) => {
        setLoading(true);
        setError(null);
        ApiService.compareAsurso({}, refresh)
            .then(setResults)
            .catch((e) => setError(errText(e, 'Не удалось выполнить сверку')))
            .finally(() => setLoading(false));
    }, []);

    useEffect(() => { run(false); }, [run]);

    // Заметка сохранена — обновляем её в уже загруженных результатах, без повторной сверки.
    const saveLocal = (item, note) => setResults((prev) => prev.map((r) => ({
        ...r, items: r.items.map((x) => (x.date === item.date && x.kind === item.kind && r.items.includes(item) ? {...x, note: note || null} : x)),
    })));

    const text = results ? reportText(results) : '';
    const copy = () => {
        navigator.clipboard?.writeText(text).then(() => {
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        });
    };
    const problems = results ? results.filter((r) => !r.ok).length : 0;

    return (
        <Stack spacing={2}>
            <Stack direction="row" spacing={1} alignItems="center" sx={{flexWrap: 'wrap'}}>
                <Typography variant="body2" color="text.secondary" sx={{flex: '1 1 300px'}}>
                    Одна запись журнала = одна пара. Считаются пары расписания, которые уже закончились; прогнозные пары не в счёт.
                    Сверяются только связанные предметы.
                </Typography>
                <Button variant="contained" onClick={() => run(true)} disabled={loading}>Проверить заново</Button>
                {text && <Button onClick={copy}>{copied ? 'Скопировано' : 'Скопировать для учебной части'}</Button>}
            </Stack>
            {error && <Alert severity="error">{error}</Alert>}
            {loading && <Box sx={{textAlign: 'center', py: 4}}><CircularProgress/></Box>}
            {!loading && results && results.length === 0 && (
                <Alert severity="info">Нет связанных предметов. Свяжите предметы на вкладке «Связка».</Alert>
            )}
            {!loading && results && results.length > 0 && (
                <Alert severity={problems ? 'warning' : 'success'}>
                    {problems ? `Расхождения: ${problems} из ${results.length} предметов` : `Всё совпадает: ${results.length} предметов`}
                </Alert>
            )}
            {!loading && results && results.map((r) => (
                <Paper key={`${r.groupId}:${r.tagId}`} variant="outlined" sx={{p: 2}}>
                    <Stack direction="row" spacing={1} alignItems="center" sx={{flexWrap: 'wrap'}}>
                        <Typography sx={{fontWeight: 600}}>{r.groupName} — {r.tagName}</Typography>
                        {r.error ? <Chip size="small" color="warning" label="не удалось сверить"/>
                            : r.ok ? <Chip size="small" color="success" label="совпадает"/>
                                : <Chip size="small" color="error" label={`расхождений: ${r.items.length}`}/>}
                        {!r.error && (
                            <Typography variant="caption" color="text.secondary">
                                расписание: {r.scheduleLessons} пар · журнал: {r.journalLessons} пар
                                {r.journalPlannedHours != null ? ` · план журнала: ${r.journalPlannedHours} ч` : ''}
                            </Typography>
                        )}
                    </Stack>
                    {r.error && <Typography variant="body2" color="text.secondary" sx={{mt: 0.5}}>{r.error}</Typography>}
                    {r.items.map((i) => <ItemRow key={i.date + i.kind} r={r} i={i} onSaved={saveLocal}/>)}
                </Paper>
            ))}
        </Stack>
    );
};

// ---------- Посещаемость ----------

const ABS = {IsAbsentByNotValidReason: 'Н', IsAbsentByValidReason: 'У', IsLate: 'опоздал', SickLeave: 'болезнь'};
const ACTION = {
    SET: {label: 'поставить', color: 'success'},
    CHANGE: {label: 'изменить', color: 'primary'},
    REMOVE: {label: 'снять отметку', color: 'warning'},
    OK: {label: 'уже стоит', color: 'default'},
    CONFLICT: {label: 'в журнале другое', color: 'error'},
    BLANK: {label: 'не заполнено', color: 'warning'},
    UNKNOWN: {label: 'непонятно', color: 'warning'},
    UNMATCHED: {label: 'нет в журнале', color: 'error'},
    NOT_ENROLLED: {label: 'не числится в группе', color: 'default'},
};
const STATUS = {
    COUNT_MISMATCH: 'Столбцов в таблице и пар в журнале разное число — сопоставить нельзя',
    NO_JOURNAL_LESSON: 'В таблице есть, а пар в журнале в этот день нет',
    NO_SHEET_COLUMN: 'В журнале пара есть, а в таблице за этот день не отмечено',
    FILLED_IN_JOURNAL: 'В таблице не отмечено, но в журнале отметки уже стоят',
};

const Cell = ({c, canWrite, busy, onApply}) => {
    const a = ACTION[c.action] || {label: c.action, color: 'default'};
    return (
        <Box sx={{minWidth: 130}}>
            <Typography variant="caption" color="text.secondary">таблица: {c.sheet || '—'}{c.current ? ` · журнал: ${ABS[c.current] || c.current}` : ''}</Typography>
            <Box>
                <Chip size="small" color={a.color} variant={c.action === 'OK' ? 'outlined' : 'filled'}
                      label={c.action === 'SET' ? `поставить ${ABS[c.desired]}` : c.action === 'CHANGE' ? `изменить на ${ABS[c.desired]}` : a.label}/>
                {(c.action === 'SET' || c.action === 'CHANGE' || c.action === 'REMOVE') && (
                    <Button size="small" variant="contained" sx={{ml: 0.5, py: 0, minWidth: 0}} disabled={!canWrite || busy} onClick={onApply}>
                        Подтвердить
                    </Button>
                )}
            </Box>
        </Box>
    );
};

// Автосинхронизация предмета с Google-таблицей: раз в 5 минут сервер смотрит, изменилась ли таблица, и переносит в АСУ РСО отметки Н/У и оценки по связкам «таблица».
const SheetAuto = ({selected}) => {
    const [st, setSt] = useState(null);
    const [error, setError] = useState(null);
    const [busy, setBusy] = useState(false);

    const load = useCallback(() => {
        if (!selected) return;
        ApiService.getAsursoSheetAuto(selected.groupId, selected.tagId).then(setSt).catch((e) => setError(errText(e, 'Не удалось получить состояние')));
    }, [selected]);

    useEffect(() => { setSt(null); setError(null); load(); }, [load]);

    // Пока идёт прогон, обновляем состояние.
    useEffect(() => {
        if (!st || !st.running) return undefined;
        const t = setInterval(load, 3000);
        return () => clearInterval(t);
    }, [st, load]);

    if (!selected) return null;
    const toggle = async (enabled) => {
        if (enabled && !await confirmDialog('Включить автосинхронизацию? Раз в 5 минут сервер будет проверять таблицу и сам ставить в АСУ РСО недостающие отметки Н/У и оценки (по связкам «таблица»), снимать и менять отметки, которые поставил сам, когда вы правите таблицу. Первый прогон сразу перенесёт всё, что сейчас не совпадает. Конфликты (в журнале стоит не то, что ставил сервер) не трогаются, о них придёт сообщение администраторам.')) return;
        setBusy(true);
        ApiService.setAsursoSheetAuto({groupId: selected.groupId, tagId: selected.tagId, enabled}).then(setSt).catch((e) => setError(errText(e, 'Не удалось изменить'))).finally(() => setBusy(false));
    };
    const run = () => {
        setBusy(true);
        ApiService.runAsursoSheetAuto(selected.groupId, selected.tagId).then(setSt).catch((e) => setError(errText(e, 'Не удалось запустить'))).finally(() => setBusy(false));
    };

    return (
        <Paper variant="outlined" sx={{p: 1.5}}>
            <Stack direction="row" spacing={1.5} alignItems="center" sx={{flexWrap: 'wrap', rowGap: 1}}>
                <FormControlLabel control={<Switch checked={Boolean(st && st.enabled)} disabled={!st || busy} onChange={(e) => toggle(e.target.checked)}/>}
                                  label="Автоматически переносить изменения из таблицы (проверка раз в 5 минут)"/>
                {st && st.enabled && <Button size="small" disabled={busy || st.running} onClick={run}>Проверить сейчас</Button>}
                {st && st.running && <><CircularProgress size={18}/><Typography variant="body2">идёт перенос…</Typography></>}
            </Stack>
            {st && st.enabled && st.lastRunAt && (
                <Typography variant="caption" color="text.secondary" sx={{display: 'block', mt: 0.5}}>
                    Последняя проверка: {new Date(st.lastRunAt).toLocaleString('ru-RU')} — {st.lastResult}
                </Typography>
            )}
            {error && <Alert severity="error" sx={{mt: 1}} onClose={() => setError(null)}>{error}</Alert>}
        </Paper>
    );
};

// «Поставить как…»: на пару журнала, где в таблице за день не отмечено, ставятся такие же отметки, как в выбранном столбце таблицы (другая пара).
const CopyMarks = ({selected, lesson, columns, date, canWrite, onDone}) => {
    const [source, setSource] = useState('');
    const [preview, setPreview] = useState(null);
    const [loading, setLoading] = useState(false);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState(null);
    const [result, setResult] = useState(null);

    const options = columns.filter((c) => c.date !== date && c.filled > 0);
    const label = (c) => `${fmtDate(c.date)} · пара ${c.ordinal + 1}${c.start ? ` (${c.start})` : ''}`;

    const show = (value) => {
        setSource(value);
        setPreview(null);
        setResult(null);
        setError(null);
        if (value === '') return;
        setLoading(true);
        ApiService.getAsursoAttendanceCopy(selected.groupId, selected.tagId, lesson.id, value)
            .then(setPreview)
            .catch((e) => setError(errText(e, 'Не удалось построить предпросмотр')))
            .finally(() => setLoading(false));
    };

    const apply = async () => {
        const c = options.find((o) => o.index === source);
        if (!await confirmDialog(`Поставить на пару ${lesson.start || ''} за ${fmtDate(date)} отметки как на паре ${c ? label(c) : ''}: ${preview.toSet} шт.?`)) return;
        setBusy(true);
        setResult(null);
        ApiService.applyAsursoAttendanceCopy({groupId: selected.groupId, tagId: selected.tagId, lessonId: lesson.id, sourceColumn: source})
            .then((r) => { setResult(r.errors.length ? {severity: 'error', text: `Поставлено: ${r.applied}. Ошибка: ${r.errors.join('; ')}`} : {severity: 'success', text: `Поставлено в журнал: ${r.applied}`}); onDone(); })
            .catch((e) => setResult({severity: 'error', text: errText(e, 'Не удалось поставить отметки')}))
            .finally(() => setBusy(false));
    };

    return (
        <Box sx={{py: 1, borderTop: 1, borderColor: 'divider'}}>
            <Stack direction="row" spacing={1.5} alignItems="center" sx={{flexWrap: 'wrap', rowGap: 1}}>
                <Typography sx={{fontWeight: 600, minWidth: 110}}>Пара {lesson.start || '—'}</Typography>
                <TextField select size="small" sx={{minWidth: 280}} label="Поставить как…" value={source} onChange={(e) => show(e.target.value)} SelectProps={{native: true}} InputLabelProps={{shrink: true}}>
                    <option value="">— выберите пару из таблицы —</option>
                    {options.map((c) => <option key={c.index} value={c.index}>{label(c)} · отметок в таблице: {c.filled}</option>)}
                </TextField>
                {loading && <CircularProgress size={20}/>}
            </Stack>
            {error && <Alert severity="error" sx={{mt: 1}}>{error}</Alert>}
            {result && <Alert severity={result.severity} sx={{mt: 1}} onClose={() => setResult(null)}>{result.text}</Alert>}
            {preview && (
                <Box sx={{mt: 1}}>
                    <Stack direction="row" spacing={1} sx={{flexWrap: 'wrap', rowGap: 0.5, mb: 0.5}}>
                        <Chip size="small" color="success" label={`поставить: ${preview.toSet}`}/>
                        {preview.conflicts > 0 && <Chip size="small" color="error" label={`в журнале другое (пропуск): ${preview.conflicts}`}/>}
                        {preview.blanks > 0 && <Chip size="small" color="warning" label={`не заполнено: ${preview.blanks}`}/>}
                        {preview.notEnrolled > 0 && <Chip size="small" variant="outlined" label={`не числятся в группе: ${preview.notEnrolled}`}/>}
                    </Stack>
                    {preview.rows.filter((r) => r.cells[0].action === 'SET' || r.cells[0].action === 'CONFLICT').map((r) => (
                        <Typography key={r.student} variant="body2" sx={{pl: 1}}>
                            {r.student}: {r.cells[0].action === 'SET' ? `поставить ${ABS[r.cells[0].desired]}` : `в журнале уже ${ABS[r.cells[0].current] || r.cells[0].current}, а по образцу ${ABS[r.cells[0].desired] || '—'}`}
                        </Typography>
                    ))}
                    <Button variant="contained" sx={{mt: 1}} disabled={!canWrite || busy || preview.toSet === 0} onClick={apply}>Поставить как на выбранной паре ({preview.toSet})</Button>
                    {busy && <CircularProgress size={20} sx={{ml: 1}}/>}
                </Box>
            )}
        </Box>
    );
};

const AttendanceTab = () => {
    const [options, setOptions] = useState([]);
    const [selected, setSelected] = useState(null);
    const [preview, setPreview] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [busy, setBusy] = useState(false);
    const [result, setResult] = useState(null);
    const [job, setJob] = useState(null);

    useEffect(() => {
        ApiService.getAsursoLinks(false).then((groups) => {
            const list = [];
            groups.forEach((g) => g.subjects.forEach((s) => {
                if (s.found && s.sheetTitle) list.push({groupId: g.groupId, tagId: s.tagId, label: `${g.groupName} — ${s.tagName}`});
            }));
            setOptions(list);
            if (list.length === 1) setSelected(list[0]);
        }).catch((e) => setError(errText(e, 'Не удалось загрузить предметы')));
    }, []);

    const run = (refresh) => {
        if (!selected) return;
        setLoading(true);
        setError(null);
        ApiService.getAsursoAttendance(selected.groupId, selected.tagId, refresh)
            .then(setPreview)
            .catch((e) => { setPreview(null); setError(errText(e, 'Не удалось построить предпросмотр')); })
            .finally(() => setLoading(false));
    };

    // «Поставить везде»: запись идёт в фоне (минуты), страница опрашивает прогресс. Если страницу перезагрузили — прогресс подхватывается.
    const showJob = useCallback((j) => {
        setJob(j);
        if (j.state === 'DONE' || j.state === 'FAILED') {
            const skipped = j.skippedDates && j.skippedDates.length
                ? `. Пропущено дат: ${j.skippedDates.length} (${j.skippedDates.map((x) => { const [d, why] = x.split(': '); return `${fmtDate(d)} — ${STATUS[why] || why}`; }).join('; ')})` : '';
            setResult(j.errors.length
                ? {severity: 'error', text: `Поставлено: ${j.applied} из ${j.total}. Остановлено: ${j.errors.join('; ')}${skipped}`}
                : {severity: 'success', text: `Поставлено в журнал: ${j.applied}${skipped}`});
        }
    }, []);

    useEffect(() => {
        if (!selected || !job || job.state !== 'RUNNING') return undefined;
        const timer = setInterval(() => {
            ApiService.getAsursoAttendanceAllStatus(selected.groupId, selected.tagId)
                .then((j) => { if (j) showJob(j); if (j && j.state !== 'RUNNING') run(true); })
                .catch(() => {});
        }, 2500);
        return () => clearInterval(timer);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selected, job, showJob]);

    useEffect(() => {
        setJob(null);
        if (!selected) return;
        ApiService.getAsursoAttendanceAllStatus(selected.groupId, selected.tagId).then((j) => { if (j && j.state === 'RUNNING') setJob(j); }).catch(() => {});
    }, [selected]);

    const applyAll = async (question) => {
        if (!await confirmDialog(question)) return;
        setResult(null);
        ApiService.startAsursoAttendanceAll(selected.groupId, selected.tagId)
            .then(setJob)
            .catch((e) => setResult({severity: 'error', text: errText(e, 'Не удалось запустить запись')}));
    };

    // Подтвердить и поставить: одну отметку (lessonId + studentId) или все «поставить» за дату. Журнал меняется только здесь.
    const apply = async (date, lessonId, studentId, question) => {
        if (question && !await confirmDialog(question)) return;
        setBusy(true);
        setResult(null);
        ApiService.applyAsursoAttendance({groupId: selected.groupId, tagId: selected.tagId, date, lessonId, studentId})
            .then((r) => setResult(r.errors.length
                ? {severity: 'error', text: `Поставлено: ${r.applied}. Ошибка: ${r.errors.join('; ')}`}
                : {severity: 'success', text: `Поставлено в журнал: ${r.applied}${r.skippedDates && r.skippedDates.length ? `. Пропущено дат: ${r.skippedDates.length} (${r.skippedDates.map((x) => { const [d, why] = x.split(': '); return `${fmtDate(d)} — ${STATUS[why] || why}`; }).join('; ')})` : ''}`}))
            .catch((e) => setResult({severity: 'error', text: errText(e, 'Не удалось поставить отметку')}))
            .finally(() => { setBusy(false); run(true); });
    };

    return (
        <Stack spacing={2}>
            <Typography variant="body2" color="text.secondary">
                Предпросмотр: что по вашей Google-таблице нужно отметить в журнале. «н» → Н, «у» → У, «б» и оценка → в журнале ничего.
                Пустые ячейки подсвечены — их отметьте вручную. В журнал отметка попадает только после вашего нажатия «Подтвердить»: ставятся только Н и У и только там, где в журнале сейчас пусто.
            </Typography>
            {options.length === 0 && !error && <Alert severity="info">Нет предметов с привязанным листом. Выберите лист на вкладке «Связка».</Alert>}
            <Stack direction="row" spacing={1} alignItems="center" sx={{flexWrap: 'wrap'}}>
                <Autocomplete size="small" sx={{flex: '1 1 300px'}} options={options} value={selected} onChange={(_, v) => { setSelected(v); setPreview(null); }}
                              getOptionLabel={(o) => o.label} isOptionEqualToValue={(a, b) => a.tagId === b.tagId && a.groupId === b.groupId}
                              renderInput={(p) => <TextField {...p} label="Группа и предмет"/>}/>
                <Button variant="contained" disabled={!selected || loading} onClick={() => run(true)}>Показать</Button>
            </Stack>
            <SheetAuto selected={selected}/>
            {error && <Alert severity="error">{error}</Alert>}
            {result && <Alert severity={result.severity} onClose={() => setResult(null)}>{result.text}</Alert>}
            {loading && <Box sx={{textAlign: 'center', py: 4}}><CircularProgress/></Box>}
            {preview && !loading && (
                <>
                    {!preview.writeEnabled && <Alert severity="info">Запись в журнал выключена на сервере — доступен только предпросмотр.</Alert>}
                    {preview.unmatchedSheetStudents.length > 0 && (
                        <Alert severity="warning">Нет в журнале (по ФИО): {preview.unmatchedSheetStudents.join('; ')}</Alert>
                    )}
                    {preview.unmatchedJournalStudents.length > 0 && (
                        <Alert severity="info">Нет в таблице: {preview.unmatchedJournalStudents.join('; ')}</Alert>
                    )}
                    {preview.dates.length === 0 && <Alert severity="info">Нет дат для сверки.</Alert>}
                    {(() => {
                        const today = todayIso();
                        const ready = preview.dates.filter((d) => d.date <= today && d.status === 'READY' && d.conflicts === 0 && d.toSet > 0);
                        const total = ready.reduce((n, d) => n + d.toSet, 0);
                        const skippedConflicts = preview.dates.filter((d) => d.date <= today && d.status === 'READY' && d.conflicts > 0).length;
                        return (
                            <Paper variant="outlined" sx={{p: 2}}>
                                <Stack direction="row" spacing={2} alignItems="center" sx={{flexWrap: 'wrap', rowGap: 1}}>
                                    <Button variant="contained" disabled={!preview.writeEnabled || busy || total === 0 || (job && job.state === 'RUNNING')}
                                            onClick={() => applyAll(`Поставить в журнал АСУ РСО все недостающие отметки Н/У: ${total} шт. за ${ready.length} дат(ы)? Даты с конфликтами и пустые ячейки пропускаются. Запись идёт в фоне, страницу можно не закрывать.`)}>
                                        Поставить везде, где нет конфликтов ({total})
                                    </Button>
                                    <Typography variant="body2" color="text.secondary">
                                        {total === 0 ? 'Нечего ставить.' : `Будет поставлено ${total} отметок за ${ready.length} дат(ы).`}
                                        {skippedConflicts > 0 && ` Пропущено дат с конфликтами: ${skippedConflicts}.`}
                                    </Typography>
                                    {(busy || (job && job.state === 'RUNNING')) && <CircularProgress size={22}/>}
                                    {job && job.state === 'RUNNING' && <Typography variant="body2">Идёт запись: {job.applied} из {job.total || '…'}</Typography>}
                                </Stack>
                            </Paper>
                        );
                    })()}
                    {preview.dates.map((d) => (
                        <Accordion key={d.date} variant="outlined" disableGutters>
                            <AccordionSummary>
                                <Stack direction="row" spacing={1} alignItems="center" sx={{flexWrap: 'wrap', width: '100%'}}>
                                    <Typography sx={{minWidth: 150, fontWeight: 600}}>{fmtDate(d.date)}</Typography>
                                    {d.status !== 'READY' && <Chip size="small" color={d.status === 'FILLED_IN_JOURNAL' ? 'success' : 'warning'} variant={d.status === 'FILLED_IN_JOURNAL' ? 'outlined' : 'filled'} label={STATUS[d.status]}/>}
                                    {d.status === 'READY' && d.toSet > 0 && <Chip size="small" color="success" label={`к записи: ${d.toSet}`}/>}
                                    {d.status === 'READY' && d.blanks > 0 && <Chip size="small" color="warning" label={`не заполнено: ${d.blanks}`}/>}
                                    {d.status === 'READY' && d.conflicts > 0 && <Chip size="small" color="error" label={`конфликтов: ${d.conflicts}`}/>}
                                    {d.status === 'READY' && d.toSet === 0 && d.blanks === 0 && d.conflicts === 0 && <Chip size="small" variant="outlined" label="всё совпадает"/>}
                                    <Typography variant="caption" color="text.secondary">пар в журнале: {d.lessons.length}, столбцов в таблице: {d.sheetColumns}</Typography>
                                    {d.scheduleLessons != null && (
                                        <Chip size="small" variant="outlined"
                                              color={d.scheduleLessons === 0 && (d.status === 'NO_JOURNAL_LESSON' || d.lessons.length > 0) ? 'error' : d.status === 'NO_JOURNAL_LESSON' ? 'info' : 'default'}
                                              label={d.scheduleLessons > 0 ? `по расписанию: ${d.scheduleLessons} ${d.scheduleLessons === 1 ? 'пара' : 'пары'}`
                                                  : d.schedulePredicted > 0 ? 'по расписанию: только прогноз' : 'по расписанию пары не было'}/>
                                    )}
                                </Stack>
                            </AccordionSummary>
                            {d.status === 'NO_JOURNAL_LESSON' && d.scheduleLessons != null && (
                                <AccordionDetails>
                                    {d.scheduleLessons === 0
                                        ? <Alert severity="warning">По расписанию колледжа в этот день пары у этой группы по этому предмету не было{d.schedulePredicted > 0 ? ' (есть только прогнозная)' : ''}, а в таблице за этот день есть отметки. Возможно, дата в таблице указана ошибочно или пара была перенесена.</Alert>
                                        : <Alert severity="info">По расписанию колледжа в этот день была пара ({d.scheduleLessons}): создайте её в журнале, тогда отметки из таблицы можно будет поставить.</Alert>}
                                </AccordionDetails>
                            )}
                            {d.status === 'NO_SHEET_COLUMN' && (
                                <AccordionDetails>
                                    <Typography variant="body2" color="text.secondary" sx={{mb: 1}}>
                                        В таблице за этот день не отмечено. Можно поставить на пару такие же отметки, как на другой паре из таблицы.
                                    </Typography>
                                    {d.lessons.map((l) => (
                                        <CopyMarks key={l.id} selected={selected} lesson={l} columns={preview.columns} date={d.date} canWrite={preview.writeEnabled} onDone={() => run(true)}/>
                                    ))}
                                </AccordionDetails>
                            )}
                            {d.status === 'READY' && (
                                <AccordionDetails>
                                    {d.rows.map((r) => (
                                        <Box key={r.student} sx={{display: 'flex', gap: 2, alignItems: 'center', py: 0.5, borderTop: 1, borderColor: 'divider', flexWrap: 'wrap'}}>
                                            <Typography sx={{flex: '1 1 220px'}}>{r.student}</Typography>
                                            {r.cells.map((c, i) => (
                                                <Cell key={i} c={c} canWrite={preview.writeEnabled} busy={busy}
                                                      onApply={() => apply(d.date, d.lessons[i].id, r.studentId)}/>
                                            ))}
                                        </Box>
                                    ))}
                                    <Box sx={{mt: 1.5}}>
                                        <Button variant="contained" disabled={!preview.writeEnabled || busy || d.toSet === 0}
                                                onClick={() => apply(d.date, null, null, `Поставить в журнал АСУ РСО все отметки за ${fmtDate(d.date)} (${d.toSet})?`)}>
                                            Подтвердить и поставить все за дату ({d.toSet})
                                        </Button>
                                    </Box>
                                </AccordionDetails>
                            )}
                        </Accordion>
                    ))}
                </>
            )}
        </Stack>
    );
};

// ---------- Страница ----------

// ---------- Оценки ----------

const GACTION = {
    SET: {label: 'поставить', color: 'success'},
    UPDATE: {label: 'обновить', color: 'primary'},
    OK: {label: 'уже стоит', color: 'default'},
    CONFLICT: {label: 'в журнале другая — не трогаем', color: 'error'},
    NO_SOURCE: {label: 'нет оценки на источнике', color: 'warning'},
    NOT_ENROLLED: {label: 'не числится в группе', color: 'default'},
};
const GSTATUS = {
    NO_LINK: {label: 'источник не выбран', color: 'default'},
    NO_TASK: {label: 'у пары нет задания в журнале', color: 'warning'},
    MANY_TASKS: {label: 'у пары несколько заданий', color: 'warning'},
    READY: {label: 'есть что ставить', color: 'primary'},
    WARN: {label: 'есть замечания', color: 'warning'},
    DONE: {label: 'всё проставлено', color: 'success'},
};

// Распределение оценок: list — счётчики для 2, 3, 4, 5 (по порядку) → «5 — 3 · 4 — 7 · 3 — 4 · 2 — 1».
const distText = (list) => {
    const parts = [5, 4, 3, 2].filter((g) => list[g - 2] > 0).map((g) => `${g} — ${list[g - 2]}`);
    return parts.length ? parts.join(' · ') : 'нет';
};
// Из строк предпросмотра: что будет поставлено / что уже стоит (по оценкам 2–5).
const gradeCounts = (lessons) => {
    const pending = [0, 0, 0, 0];
    const inJournal = [0, 0, 0, 0];
    lessons.forEach((l) => l.rows.forEach((r) => {
        if ((r.action === 'SET' || r.action === 'UPDATE') && r.source) pending[r.source - 2] += 1;
        if (r.current) inJournal[r.current - 2] += 1;
    }));
    return {pending, inJournal, total: pending.reduce((a, b) => a + b, 0)};
};

// Одна ПЗ: источник оценки, предпросмотр по студентам и запись.
const GradeLesson = ({selected, lesson, articles, sheetColumns, sheetConfigured, canWrite, duplicates, onDraft, onChanged}) => {
    const [source, setSource] = useState(lesson.linkSource || '');
    const [article, setArticle] = useState(articles.find((a) => a.id === (lesson.articleId || lesson.suggestedArticleId)) || null);
    const [auto, setAuto] = useState(lesson.linkSource ? lesson.auto : true);
    // Столбец таблицы: пусто — столбец даты пары (по порядку), иначе «дата#номер».
    const [column, setColumn] = useState(lesson.sheetDate ? `${lesson.sheetDate}#${lesson.sheetOrdinal}` : '');
    const [open, setOpen] = useState(false);
    const [busy, setBusy] = useState(false);
    const [result, setResult] = useState(null);
    const st = GSTATUS[lesson.status] || {label: lesson.status, color: 'default'};

    // Выбранный (ещё не сохранённый) источник сообщаем наверх: так видно повторы сразу, а не после сохранения.
    const draftKey = source === 'PLATFORM' && article ? `P:${article.id}` : source === 'SHEET' && column ? `S:${column}` : null;
    useEffect(() => { onDraft(lesson.lessonId, draftKey); }, [draftKey]); // eslint-disable-line react-hooks/exhaustive-deps
    const changed = (lesson.linkSource || '') !== source || (source === 'PLATFORM' && (article ? article.id : null) !== (lesson.articleId || null)) || (lesson.linkSource && source === lesson.linkSource && lesson.auto !== auto) || (source === 'SHEET' && column !== (lesson.sheetDate ? `${lesson.sheetDate}#${lesson.sheetOrdinal}` : ''));

    const saveLink = (src, art) => {
        setBusy(true);
        setResult(null);
        ApiService.linkAsursoGrade({groupId: selected.groupId, tagId: selected.tagId, plannedLessonId: lesson.plannedId, topicName: lesson.topicName, source: src || null, articleId: art ? art.id : null, auto,
            sheetDate: src === 'SHEET' && column ? column.split('#')[0] : null, sheetOrdinal: src === 'SHEET' && column ? Number(column.split('#')[1]) : null})
            .then(onChanged)
            .catch((e) => setResult({severity: 'error', text: errText(e, 'Не удалось сохранить')}))
            .finally(() => setBusy(false));
    };

    const apply = async () => {
        const c = gradeCounts([lesson]);
        if (!await confirmDialog(`Поставить в журнал АСУ РСО оценки за «${lesson.topicName}»: ${c.total} шт.\nБудет поставлено: ${distText(c.pending)}.\nОценки, которые стоят в журнале и отличаются, не перезаписываются.`)) return;
        setBusy(true);
        setResult(null);
        ApiService.applyAsursoGrades({groupId: selected.groupId, tagId: selected.tagId, lessonId: lesson.lessonId})
            .then((r) => setResult(r.errors.length ? {severity: 'error', text: `Поставлено: ${r.applied}. Ошибка: ${r.errors.join('; ')}`} : {severity: 'success', text: `Поставлено в журнал: ${r.applied}`}))
            .catch((e) => setResult({severity: 'error', text: errText(e, 'Не удалось поставить оценки')}))
            .finally(() => { setBusy(false); onChanged(); });
    };

    return (
        <Paper variant="outlined" sx={{p: 1.5}}>
            <Stack direction="row" spacing={1} alignItems="center" sx={{flexWrap: 'wrap', rowGap: 1}}>
                <Typography sx={{fontWeight: 600, minWidth: 120}}>{fmtDate(lesson.date)}</Typography>
                <Typography variant="body2" sx={{flex: '1 1 260px'}}>{lesson.topicName}</Typography>
                <Chip size="small" color={st.color} label={st.label}/>
                {lesson.linkSource && lesson.status !== 'NO_TASK' && lesson.status !== 'MANY_TASKS' && (
                    <Typography variant="caption" color="text.secondary">
                        {[lesson.toSet > 0 && `поставить: ${lesson.toSet}`, lesson.toUpdate > 0 && `обновить: ${lesson.toUpdate}`, lesson.ok > 0 && `совпадает: ${lesson.ok}`,
                            lesson.conflicts > 0 && `конфликтов: ${lesson.conflicts}`, lesson.noSource > 0 && `без оценки: ${lesson.noSource}`].filter(Boolean).join(' · ')}{lesson.toSet + lesson.toUpdate > 0 && ` (поставится: ${distText(gradeCounts([lesson]).pending)})`}
                    </Typography>
                )}
            </Stack>
            <Stack direction="row" spacing={1} alignItems="center" sx={{flexWrap: 'wrap', rowGap: 1, mt: 1}}>
                <TextField select size="small" sx={{minWidth: 190}} label="Откуда оценка" value={source} onChange={(e) => setSource(e.target.value)} SelectProps={{native: true}} InputLabelProps={{shrink: true}}>
                    <option value="">— не связано —</option>
                    <option value="PLATFORM">Платформа (оценка за лекцию)</option>
                    <option value="SHEET" disabled={!sheetConfigured}>Таблица (Google-лист предмета)</option>
                </TextField>
                {source === 'SHEET' && (
                    <TextField select size="small" sx={{minWidth: 300}} label="Столбец таблицы" value={column} onChange={(e) => setColumn(e.target.value)} SelectProps={{native: true}} InputLabelProps={{shrink: true}}>
                        <option value="">Столбец даты пары ({fmtDate(lesson.date)}), по порядку</option>
                        {sheetColumns.map((c) => <option key={`${c.date}#${c.ordinal}`} value={`${c.date}#${c.ordinal}`}>{fmtDate(c.date)} · столбец {c.ordinal + 1} · оценок: {c.grades}</option>)}
                    </TextField>
                )}
                {source === 'PLATFORM' && (
                    <Autocomplete size="small" sx={{minWidth: 300, flex: '1 1 300px'}} options={articles} value={article} onChange={(_, v) => setArticle(v)}
                                  getOptionLabel={(o) => o.title} isOptionEqualToValue={(a, b) => a.id === b.id} renderInput={(p) => <TextField {...p} label="Лекция на платформе"/>}/>
                )}
                {source && <FormControlLabel control={<Switch size="small" checked={auto} onChange={(e) => setAuto(e.target.checked)}/>} label="Автообновление"/>}
                <Button size="small" variant="outlined" color={duplicates.length ? 'warning' : 'primary'} disabled={busy || !changed || (source === 'PLATFORM' && !article)}
                        onClick={async () => { if (duplicates.length && !await confirmDialog(`Этот источник уже используется для: ${duplicates.map((d) => d.topicName).join('; ')}. Оценки у этих ПЗ будут браться из одного и того же места. Всё равно сохранить?`)) return; saveLink(source, article); }}>Сохранить связку</Button>
                {lesson.suggestedArticleId && !lesson.linkSource && source === '' && (
                    <Button size="small" onClick={() => { const a = articles.find((x) => x.id === lesson.suggestedArticleId); setSource('PLATFORM'); setArticle(a || null); }}>Подставить по названию</Button>
                )}
                <Box sx={{flex: 1}}/>
                {lesson.rows.length > 0 && <Button size="small" onClick={() => setOpen(!open)}>{open ? 'Свернуть' : 'Студенты'}</Button>}
                {lesson.linkSource && (lesson.toSet + lesson.toUpdate) > 0 && (
                    <Button size="small" variant="contained" disabled={!canWrite || busy} onClick={apply}>Поставить оценки ({lesson.toSet + lesson.toUpdate})</Button>
                )}
                {busy && <CircularProgress size={20}/>}
            </Stack>
            {duplicates.length > 0 && (
                <Alert severity="warning" sx={{mt: 1}}>
                    {source === 'PLATFORM' ? 'Эта лекция уже привязана' : 'Этот столбец уже используется'} ещё для {duplicates.length === 1 ? 'одной ПЗ' : `${duplicates.length} ПЗ`}:{' '}
                    {duplicates.map((d) => `${d.topicName} (${fmtDate(d.date)})`).join('; ')}. Оценки у них будут браться из одного и того же источника — проверьте, что так и задумано.
                </Alert>
            )}
            {result && <Alert severity={result.severity} sx={{mt: 1}} onClose={() => setResult(null)}>{result.text}</Alert>}
            {open && (
                <Box sx={{mt: 1}}>
                    {lesson.rows.map((r) => {
                        const a = GACTION[r.action] || {label: r.action, color: 'default'};
                        return (
                            <Box key={r.studentId} sx={{display: 'flex', gap: 1.5, alignItems: 'center', py: 0.4, borderTop: 1, borderColor: 'divider', flexWrap: 'wrap'}}>
                                <Typography variant="body2" sx={{flex: '1 1 240px'}}>{r.student}</Typography>
                                {r.absence && <Chip size="small" variant="outlined" label={ABS[r.absence] || r.absence}/>}
                                <Typography variant="caption" color="text.secondary" sx={{minWidth: 140}}>журнал: {r.current ?? '—'} · источник: {r.source ?? '—'}</Typography>
                                <Chip size="small" color={a.color} variant={r.action === 'OK' || r.action === 'NOT_ENROLLED' ? 'outlined' : 'filled'} label={a.label}/>
                            </Box>
                        );
                    })}
                </Box>
            )}
        </Paper>
    );
};

const GradesTab = () => {
    const [options, setOptions] = useState([]);
    const [selected, setSelected] = useState(null);
    const [preview, setPreview] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [summary, setSummary] = useState(null);
    const [job, setJob] = useState(null);
    const [result, setResult] = useState(null);
    const [filter, setFilter] = useState('ALL');
    const [drafts, setDrafts] = useState({});

    useEffect(() => {
        ApiService.getAsursoLinks(false).then((groups) => {
            const list = [];
            groups.forEach((g) => g.subjects.forEach((s) => { if (s.found) list.push({groupId: g.groupId, tagId: s.tagId, label: `${g.groupName} — ${s.tagName}`}); }));
            setOptions(list);
        }).catch((e) => setError(errText(e, 'Не удалось загрузить предметы')));
        ApiService.getAsursoGradesSummary().then(setSummary).catch(() => setSummary([]));
    }, []);

    const reload = useCallback((sel) => {
        const target = sel || selected;
        if (!target) return;
        setLoading(true);
        setError(null);
        ApiService.getAsursoGrades(target.groupId, target.tagId)
            .then((p) => { setDrafts({}); setPreview(p); })
            .catch((e) => { setPreview(null); setError(errText(e, 'Не удалось построить план оценок')); })
            .finally(() => setLoading(false));
    }, [selected]);

    const open = (o) => { setSelected(o); setResult(null); setJob(null); reload(o); };

    // «Поставить по всем связанным»: запись в фоне с прогрессом.
    useEffect(() => {
        if (!selected || !job || job.state !== 'RUNNING') return undefined;
        const timer = setInterval(() => {
            ApiService.getAsursoGradesAllStatus(selected.groupId, selected.tagId).then((j) => {
                if (!j) return;
                setJob(j);
                if (j.state !== 'RUNNING') {
                    setResult(j.errors.length ? {severity: 'error', text: `Поставлено: ${j.applied} из ${j.total}. Остановлено: ${j.errors.join('; ')}`} : {severity: 'success', text: `Поставлено в журнал: ${j.applied}`});
                    reload();
                    ApiService.getAsursoGradesSummary().then(setSummary).catch(() => {});
                }
            }).catch(() => {});
        }, 2500);
        return () => clearInterval(timer);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selected, job]);

    const applyAll = async () => {
        const c = gradeCounts(preview.lessons);
        if (!await confirmDialog(`Поставить в журнал АСУ РСО оценки по всем связанным ПЗ: ${c.total} шт.\nБудет поставлено: ${distText(c.pending)}.\nОценки, которые стоят в журнале и отличаются, не перезаписываются. Запись идёт в фоне.`)) return;
        setResult(null);
        ApiService.startAsursoGradesAll(selected.groupId, selected.tagId).then(setJob).catch((e) => setResult({severity: 'error', text: errText(e, 'Не удалось запустить запись')}));
    };

    const linkSuggested = async () => {
        const todo = preview.lessons.filter((l) => !l.linkSource && l.suggestedArticleId);
        if (!await confirmDialog(`Связать ${todo.length} ПЗ с лекциями по названию (с автообновлением)?`)) return;
        setLoading(true);
        for (const l of todo) {
            // eslint-disable-next-line no-await-in-loop
            await ApiService.linkAsursoGrade({groupId: selected.groupId, tagId: selected.tagId, plannedLessonId: l.plannedId, topicName: l.topicName, source: 'PLATFORM', articleId: l.suggestedArticleId, auto: true}).catch(() => {});
        }
        reload();
    };

    const setDraft = useCallback((lessonId, key) => setDrafts((d) => (d[lessonId] === key ? d : {...d, [lessonId]: key})), []);
    // Источник каждой ПЗ с учётом несохранённого выбора; ПЗ с одним и тем же источником — повтор.
    const sourceKey = (l) => (drafts[l.lessonId] !== undefined ? drafts[l.lessonId]
        : l.linkSource === 'PLATFORM' && l.articleId ? `P:${l.articleId}` : l.linkSource === 'SHEET' && l.sheetDate ? `S:${l.sheetDate}#${l.sheetOrdinal}` : null);
    const duplicatesOf = (l) => {
        const key = sourceKey(l);
        return key ? (preview ? preview.lessons : []).filter((o) => o.lessonId !== l.lessonId && sourceKey(o) === key) : [];
    };
    const repeated = preview ? preview.lessons.filter((l) => duplicatesOf(l).length > 0).length : 0;
    const totalTodo = preview ? preview.lessons.reduce((n, l) => n + l.toSet + l.toUpdate, 0) : 0;
    const canWrite = preview && preview.writeEnabled && preview.own && preview.editable;
    const shown = preview ? preview.lessons.filter((l) => filter === 'ALL' || (filter === 'TODO' ? l.status === 'READY' : filter === 'NOLINK' ? l.status === 'NO_LINK' : l.status !== 'DONE')) : [];

    return (
        <Stack spacing={2}>
            <Typography variant="body2" color="text.secondary">
                Оценки за практические работы (темы типа ПЗ): для каждой ПЗ выберите, откуда брать оценку, — итоговая оценка за лекцию на платформе или цифры из столбца таблицы (по умолчанию столбец даты пары, но можно выбрать любой другой).
                Оценка ставится в ячейку студента на паре, в том числе тем, кто отмечен «Н» или «У» (отметка не меняется). Если на источнике оценки нет, ничего не ставится.
                Двойки с платформы уходят в журнал только по лекциям, нужным для допуска к экзамену (оценки 3–5 уходят всегда). Оценку, которая в журнале уже стоит и отличается (поставлена вручную), я не трогаю. Для платформы можно включить автообновление: оценка на платформе изменилась, журнал обновится сам (проверка раз в 5 минут).
            </Typography>
            {summary && summary.length > 0 && (
                <Paper variant="outlined" sx={{p: 2}}>
                    <Typography sx={{fontWeight: 700, mb: 1}}>Сводка по предметам</Typography>
                    {summary.map((r, i) => (
                        <Box key={`${r.groupId}-${r.tagId}`} onClick={() => open({groupId: r.groupId, tagId: r.tagId, label: `${r.groupName} — ${r.subjectName}`})}
                             sx={{display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap', px: 1, py: 0.75, borderTop: i ? 1 : 0, borderColor: 'divider', cursor: 'pointer', borderRadius: 1, '&:hover': {bgcolor: 'action.hover'}}}>
                            <Typography sx={{fontWeight: 600, minWidth: 70}}>{r.groupName}</Typography>
                            <Typography variant="body2" sx={{flex: '1 1 220px'}}>{r.subjectName}</Typography>
                            {r.error ? <Typography variant="caption" color="error">{r.error}</Typography> : (
                                <>
                                    {!r.own && <Chip size="small" variant="outlined" label="чужой предмет"/>}
                                    <Chip size="small" variant="outlined" label={`ПЗ: ${r.pz}, связано: ${r.linked}`}/>
                                    {r.toSet + r.toUpdate > 0
                                        ? <Chip size="small" color="primary" label={`не проставлено: ${r.toSet + r.toUpdate} (${distText(r.pending)})`}/>
                                        : r.linked > 0 && <Chip size="small" color="success" variant="outlined" label="всё проставлено"/>}
                                    {r.inJournal.some((x) => x > 0) && <Typography variant="caption" color="text.secondary">в журнале: {distText(r.inJournal)}</Typography>}
                                    {r.conflicts > 0 && <Chip size="small" color="error" label={`конфликтов: ${r.conflicts}`}/>}
                                    {r.pz > r.linked && <Chip size="small" color="warning" variant="outlined" label={`не связано: ${r.pz - r.linked}`}/>}
                                </>
                            )}
                        </Box>
                    ))}
                </Paper>
            )}
            <Autocomplete size="small" options={options} value={selected} onChange={(_, v) => { if (v) open(v); else { setSelected(null); setPreview(null); } }} getOptionLabel={(o) => o.label}
                          isOptionEqualToValue={(a, b) => a.tagId === b.tagId && a.groupId === b.groupId} renderInput={(p) => <TextField {...p} label="Группа и предмет"/>}/>
            <SheetAuto selected={selected}/>
            {error && <Alert severity="error">{error}</Alert>}
            {result && <Alert severity={result.severity} onClose={() => setResult(null)}>{result.text}</Alert>}
            {loading && <Box sx={{textAlign: 'center', py: 3}}><CircularProgress/></Box>}
            {preview && !loading && (
                <>
                    {!preview.own && <Alert severity="warning">Этот предмет в журнале ведёт {preview.teacher} — оценки в него не пишутся, доступен только просмотр.</Alert>}
                    {!preview.writeEnabled && <Alert severity="info">Запись в журнал выключена на сервере — доступен только предпросмотр.</Alert>}
                    {repeated > 0 && <Alert severity="warning">Один и тот же источник оценки (лекция или столбец таблицы) выбран у нескольких ПЗ: {repeated} ПЗ. Такие карточки помечены предупреждением.</Alert>}
                    {preview.unmatchedPlatform.length > 0 && <Alert severity="info">Студенты платформы, не найденные в журнале по ФИО: {preview.unmatchedPlatform.join('; ')}</Alert>}
                    {preview.lessons.length === 0 && <Alert severity="info">У проведённых пар этого предмета нет тем типа ПЗ.</Alert>}
                    {preview.lessons.length > 0 && (() => {
                        const c = gradeCounts(preview.lessons);
                        const forgotten = preview.lessons.reduce((n, l) => n + l.toSet + l.toUpdate, 0);
                        const unlinked = preview.lessons.filter((l) => !l.linkSource).length;
                        return (
                            <Paper variant="outlined" sx={{p: 2}}>
                                <Stack direction="row" spacing={3} sx={{flexWrap: 'wrap', rowGap: 1.5}}>
                                    <Box>
                                        <Typography variant="caption" color="text.secondary">Не проставлено в журнал (оценка есть на источнике)</Typography>
                                        <Typography variant="h5" sx={{fontWeight: 800, color: forgotten ? 'warning.main' : 'success.main'}}>{forgotten}</Typography>
                                        <Typography variant="caption" color="text.secondary">{forgotten ? `из них обновить: ${preview.lessons.reduce((n, l) => n + l.toUpdate, 0)}` : 'ничего не пропущено'}</Typography>
                                    </Box>
                                    <Box>
                                        <Typography variant="caption" color="text.secondary">Будет поставлено</Typography>
                                        <Typography variant="h6">{distText(c.pending)}</Typography>
                                    </Box>
                                    <Box>
                                        <Typography variant="caption" color="text.secondary">Уже стоит в журнале (связанные ПЗ)</Typography>
                                        <Typography variant="h6">{distText(c.inJournal)}</Typography>
                                    </Box>
                                    {unlinked > 0 && (
                                        <Box>
                                            <Typography variant="caption" color="text.secondary">ПЗ без источника (оценки за них не проверяются)</Typography>
                                            <Typography variant="h6" sx={{color: 'warning.main'}}>{unlinked}</Typography>
                                        </Box>
                                    )}
                                </Stack>
                            </Paper>
                        );
                    })()}
                    {preview.lessons.length > 0 && (
                        <Stack direction="row" spacing={1} alignItems="center" sx={{flexWrap: 'wrap', rowGap: 1}}>
                            <ToggleButtonGroup size="small" exclusive value={filter} onChange={(_, v) => v && setFilter(v)}>
                                <ToggleButton value="ALL">Все ПЗ</ToggleButton>
                                <ToggleButton value="TODO">Есть что ставить</ToggleButton>
                                <ToggleButton value="NOLINK">Не связаны</ToggleButton>
                                <ToggleButton value="OPEN">Не закрыты</ToggleButton>
                            </ToggleButtonGroup>
                            <Box sx={{flex: 1}}/>
                            {preview.lessons.some((l) => !l.linkSource && l.suggestedArticleId) && <Button size="small" variant="outlined" onClick={linkSuggested}>Связать по названию</Button>}
                            <Button variant="contained" disabled={!canWrite || totalTodo === 0 || (job && job.state === 'RUNNING')} onClick={applyAll}>Поставить по всем связанным ({totalTodo})</Button>
                            {job && job.state === 'RUNNING' && <><CircularProgress size={20}/><Typography variant="body2">Идёт запись: {job.applied} из {job.total || '…'}</Typography></>}
                        </Stack>
                    )}
                    {shown.map((l) => <GradeLesson key={`${l.lessonId}-${l.linkSource}-${l.articleId}-${l.sheetDate}-${l.sheetOrdinal}`} selected={selected} lesson={l} articles={preview.articles} sheetColumns={preview.sheetColumns} sheetConfigured={preview.sheetConfigured} canWrite={canWrite} duplicates={duplicatesOf(l)} onDraft={setDraft} onChanged={() => { reload(); ApiService.getAsursoGradesSummary().then(setSummary).catch(() => {}); }}/>)}
                </>
            )}
        </Stack>
    );
};

// ---------- Темы ----------

const TYPE = {Lecture: 'лекция', PracticalTraining: 'практика', LaboratoryWork: 'лаб. работа', Seminar: 'семинар'};
const typeLabel = (t) => TYPE[t] || t || '—';
const todayIso = () => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`; };

const SUMMARY = {
    DONE: {label: 'всё заполнено', color: 'success'},
    TODO: {label: 'нужно расставить', color: 'primary'},
    ORDER: {label: 'нарушен порядок', color: 'error'},
    SHORT: {label: 'тем не хватает', color: 'warning'},
    EMPTY: {label: 'пар нет', color: 'default'},
    NO_PROGRAM: {label: 'нет программы', color: 'warning'},
    ERROR: {label: 'ошибка чтения', color: 'error'},
};

// Сводка по всем вашим предметам: где всё заполнено, где нужно расставить, где нарушен порядок.
const TopicsSummary = ({rows, loading, error, filter, setFilter, onOpen, onReload}) => {
    const shown = (rows || []).filter((r) => filter === 'ALL' || (filter === 'PROBLEM' ? ['TODO', 'ORDER', 'SHORT', 'NO_PROGRAM', 'ERROR'].includes(r.status) : r.status === filter));
    const count = (st) => (rows || []).filter((r) => r.status === st).length;
    return (
        <Paper variant="outlined" sx={{p: 2}}>
            <Stack direction="row" spacing={1} alignItems="center" sx={{flexWrap: 'wrap', rowGap: 1, mb: 1}}>
                <Typography sx={{fontWeight: 700}}>Сводка по предметам</Typography>
                {rows && <>
                    <Chip size="small" color="success" variant={filter === 'DONE' ? 'filled' : 'outlined'} label={`заполнено: ${count('DONE')}`} onClick={() => setFilter(filter === 'DONE' ? 'ALL' : 'DONE')}/>
                    <Chip size="small" color="primary" variant={filter === 'TODO' ? 'filled' : 'outlined'} label={`нужно расставить: ${count('TODO')}`} onClick={() => setFilter(filter === 'TODO' ? 'ALL' : 'TODO')}/>
                    <Chip size="small" color="error" variant={filter === 'ORDER' ? 'filled' : 'outlined'} label={`нарушен порядок: ${count('ORDER')}`} onClick={() => setFilter(filter === 'ORDER' ? 'ALL' : 'ORDER')}/>
                </>}
                <Box sx={{flex: 1}}/>
                <Button size="small" onClick={onReload} disabled={loading}>Обновить сводку</Button>
            </Stack>
            {loading && <Box sx={{textAlign: 'center', py: 2}}><CircularProgress size={24}/></Box>}
            {error && <Alert severity="error">{error}</Alert>}
            {!loading && rows && shown.length === 0 && <Typography variant="body2" color="text.secondary">Нет предметов с таким статусом.</Typography>}
            {!loading && shown.map((r, i) => (
                <Box key={`${r.journalGroupId}-${r.subjectId}`} onClick={() => onOpen(r)}
                     sx={{display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap', px: 1, py: 0.75, borderTop: i ? 1 : 0, borderColor: 'divider', cursor: 'pointer', borderRadius: 1, '&:hover': {bgcolor: 'action.hover'}}}>
                    <Typography sx={{fontWeight: 600, minWidth: 70}}>{r.groupName}</Typography>
                    <Typography variant="body2" sx={{flex: '1 1 220px'}}>{r.subjectName}</Typography>
                    <Chip size="small" color={SUMMARY[r.status].color} label={SUMMARY[r.status].label}/>
                    {!r.own && <Chip size="small" variant="outlined" label={`ведёт ${r.teacher}`}/>}
                    <Typography variant="caption" color="text.secondary" sx={{minWidth: 200}}>
                        {r.status === 'ERROR' ? r.error : [
                            `с темами: ${r.have}`,
                            r.toAssign > 0 && `к расстановке: ${r.toAssign}${r.create ? ` (создать пар: ${r.create})` : ''}`,
                            r.orderIssues > 0 && `порядок нарушен в парах: ${r.orderIssues}`,
                            r.noTopic > 0 && `без темы: ${r.noTopic}`,
                            r.future > 0 && `позже: ${r.future}`,
                        ].filter(Boolean).join(' · ')}
                    </Typography>
                </Box>
            ))}
        </Paper>
    );
};

const FixOrder = ({selected, upTo, preview, onDone}) => {
    const [plan, setPlan] = useState(null);
    const [loading, setLoading] = useState(false);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState(null);
    const [result, setResult] = useState(null);

    const load = () => {
        setLoading(true);
        setError(null);
        ApiService.getAsursoTopicsFix(selected.journalGroupId, selected.subjectId, upTo)
            .then(setPlan)
            .catch((e) => { setPlan(null); setError(errText(e, 'Не удалось построить план исправления')); })
            .finally(() => setLoading(false));
    };

    useEffect(() => { setPlan(null); setResult(null); setError(null); }, [selected, upTo]);

    const apply = async (lessonId, question) => {
        if (!await confirmDialog(question)) return;
        setBusy(true);
        setResult(null);
        ApiService.applyAsursoTopicsFix({journalGroupId: selected.journalGroupId, subjectId: selected.subjectId, upTo, lessonId})
            .then((r) => setResult(r.errors.length
                ? {severity: 'error', text: `Исправлено пар: ${r.applied}. Остановлено: ${r.errors.join('; ')}`}
                : {severity: 'success', text: `Исправлено пар: ${r.applied}. Проверьте в журнале, что темы встали, а оценки на месте.`}))
            .catch((e) => setResult({severity: 'error', text: errText(e, 'Не удалось исправить порядок')}))
            .finally(() => { setBusy(false); load(); onDone(); });
    };

    const canWrite = preview.writeEnabled && preview.own && preview.editable && preview.programApproved;
    const partsText = (parts) => (parts.length ? parts.map((p) => `${p.name} (${p.hours} ч)`).join('; ') : '— тем нет —');

    return (
        <Paper variant="outlined" sx={{p: 2, borderColor: 'error.main'}}>
            <Typography sx={{fontWeight: 700}}>Исправление порядка</Typography>
            <Typography variant="body2" color="text.secondary" sx={{mb: 1}}>
                Пары и оценки не удаляются: у пар, начиная с первого нарушения, темы переназначаются по порядку программы, а тип пары (лекция, практика, консультация) приводится к типу новой темы. Пары до нарушения остаются как есть. Для пробы лучше взять самую первую пару плана: у неё тема точно ни у кого больше не стоит.
            </Typography>
            {!preview.own && <Alert severity="warning" sx={{mb: 1}}>Этот предмет в журнале ведёт {preview.teacher} — запись тем выключена.</Alert>}
            {!plan && <Button variant="outlined" color="error" disabled={loading} onClick={load}>Показать план исправления</Button>}
            {loading && <CircularProgress size={22} sx={{ml: 1}}/>}
            {error && <Alert severity="error" sx={{mt: 1}}>{error}</Alert>}
            {result && <Alert severity={result.severity} sx={{mt: 1}} onClose={() => setResult(null)}>{result.text}</Alert>}
            {plan && plan.changes.length === 0 && <Alert severity="success" sx={{mt: 1}}>Исправлять нечего.</Alert>}
            {plan && plan.changes.length > 0 && (
                <Stack spacing={1} sx={{mt: 1}}>
                    {plan.unresolved > 0 && <Alert severity="warning">Тем в программе не хватает для {plan.unresolved} пар: они останутся как есть.</Alert>}
                    <Paper variant="outlined">
                        {plan.changes.map((c, i) => (
                            <Box key={c.lessonId || `s${c.scheduleLessonId}`} sx={{px: 2, py: 1, borderTop: i ? 1 : 0, borderColor: 'divider'}}>
                                <Stack direction="row" spacing={1} alignItems="center" sx={{flexWrap: 'wrap', rowGap: 0.5}}>
                                    <Typography sx={{fontWeight: 600, minWidth: 130}}>{fmtDate(c.date)}</Typography>
                                    <Typography variant="body2" color="text.secondary">{c.start || ''} · {typeLabel(c.lessonType)} · {c.duration} ч</Typography>
                                    {c.hasMarks && <Chip size="small" color="info" variant="outlined" label="есть оценки — останутся"/>}
                                    {c.create && <Chip size="small" variant="outlined" label="пары в журнале нет — будет создана"/>}
                                    {c.typeMismatch && <Chip size="small" color="warning" variant="outlined" label={`тип пары изменится: ${typeLabel(c.lessonType)} → ${typeLabel(c.after[0].type)}`}/>}
                                    <Box sx={{flex: 1}}/>
                                    {!c.create && (
                                        <Button size="small" disabled={!canWrite || busy}
                                                onClick={() => apply(c.lessonId, `Исправить только пару ${fmtDate(c.date)}? Это проба: проверьте в журнале, что тема сменилась${c.hasMarks ? ' и оценки на месте' : ''}.`)}>
                                            Исправить только эту пару
                                        </Button>
                                    )}
                                </Stack>
                                <Typography variant="body2" color="text.secondary" sx={{pl: 1}}>было: {partsText(c.before)}</Typography>
                                <Typography variant="body2" sx={{pl: 1}}>станет: {partsText(c.after)}</Typography>
                            </Box>
                        ))}
                    </Paper>
                    <Stack direction="row" spacing={1} alignItems="center">
                        <Button variant="contained" color="error" disabled={!canWrite || busy}
                                onClick={() => apply(null, `Исправить порядок тем на ${plan.changes.length} пар(ах)? Пары и оценки не удаляются, меняются только темы.`)}>
                            Исправить все ({plan.changes.length})
                        </Button>
                        {busy && <CircularProgress size={22}/>}
                    </Stack>
                </Stack>
            )}
        </Paper>
    );
};

// Расстановка тем рабочей программы по парам журнала вместо ручного перетаскивания: темы идут по порядку, пары — по дате.
const TopicsTab = () => {
    const [options, setOptions] = useState([]);
    const [selected, setSelected] = useState(null);
    const [upTo, setUpTo] = useState(todayIso());
    const [preview, setPreview] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [busy, setBusy] = useState(false);
    const [result, setResult] = useState(null);
    const [showHave, setShowHave] = useState(true);
    const [summary, setSummary] = useState(null);
    const [summaryLoading, setSummaryLoading] = useState(false);
    const [summaryError, setSummaryError] = useState(null);
    const [summaryFilter, setSummaryFilter] = useState('ALL');

    useEffect(() => {
        ApiService.getAsursoLinks(false).then((groups) => {
            const list = [];
            groups.forEach((g) => {
                if (g.journalGroupId == null) return;
                g.journalSubjects.filter((s) => s.mine).forEach((s) => list.push({journalGroupId: g.journalGroupId, subjectId: s.id, label: `${g.groupName} — ${s.name}`}));
            });
            setOptions(list);
        }).catch((e) => setError(errText(e, 'Не удалось загрузить предметы')));
    }, []);

    const loadSummary = useCallback(() => {
        setSummaryLoading(true);
        setSummaryError(null);
        ApiService.getAsursoTopicsSummary(upTo)
            .then(setSummary)
            .catch((e) => { setSummary(null); setSummaryError(errText(e, 'Не удалось построить сводку')); })
            .finally(() => setSummaryLoading(false));
    }, [upTo]);

    useEffect(() => { loadSummary(); }, [loadSummary]);

    const open = (row) => {
        const o = {journalGroupId: row.journalGroupId, subjectId: row.subjectId, label: `${row.groupName} — ${row.subjectName}`};
        setSelected(o);
        setResult(null);
        setLoading(true);
        setError(null);
        ApiService.getAsursoTopics(o.journalGroupId, o.subjectId, upTo)
            .then(setPreview)
            .catch((e) => { setPreview(null); setError(errText(e, 'Не удалось построить план')); })
            .finally(() => setLoading(false));
    };

    const run = () => {
        if (!selected) return;
        setLoading(true);
        setError(null);
        ApiService.getAsursoTopics(selected.journalGroupId, selected.subjectId, upTo)
            .then(setPreview)
            .catch((e) => { setPreview(null); setError(errText(e, 'Не удалось построить план')); })
            .finally(() => setLoading(false));
    };

    // Журнал меняется только здесь: темы встают в пары без тем, по порядку программы. limit — только первые N пар (проба).
    const apply = async (limit, question) => {
        if (!await confirmDialog(question)) return;
        setBusy(true);
        setResult(null);
        ApiService.applyAsursoTopics({journalGroupId: selected.journalGroupId, subjectId: selected.subjectId, upTo, limit})
            .then((r) => setResult(r.errors.length
                ? {severity: 'error', text: `Расставлено пар: ${r.applied}. Ошибка: ${r.errors.join('; ')}`}
                : {severity: 'success', text: `Расставлено пар в журнале: ${r.applied}`}))
            .catch((e) => setResult({severity: 'error', text: errText(e, 'Не удалось расставить темы')}))
            .finally(() => { setBusy(false); run(); loadSummary(); });
    };

    const canApply = preview && preview.writeEnabled && preview.own && preview.editable && preview.programApproved && !preview.outOfOrder && preview.assignments.length > 0;

    return (
        <Stack spacing={2}>
            <Typography variant="body2" color="text.secondary">
                Показывает, какие темы рабочей программы встанут на пары журнала, где тем ещё нет: темы идут строго по порядку программы, пары — по дате.
                Это то же самое, что перетаскивание темы на пару. Пары, где темы уже стоят, не трогаются. Если пары в журнале ещё нет (слот расписания), запись пары создаётся вместе с темой, как при перетаскивании. В журнале ничего не меняется, пока вы не нажмёте «Расставить».
            </Typography>
            <TopicsSummary rows={summary} loading={summaryLoading} error={summaryError} filter={summaryFilter} setFilter={setSummaryFilter} onOpen={open} onReload={loadSummary}/>
            <Stack direction="row" spacing={1} alignItems="center" sx={{flexWrap: 'wrap', rowGap: 1}}>
                <Autocomplete size="small" sx={{flex: '1 1 320px'}} options={options} value={selected} onChange={(_, v) => { setSelected(v); setPreview(null); setResult(null); }}
                              getOptionLabel={(o) => o.label} isOptionEqualToValue={(a, b) => a.journalGroupId === b.journalGroupId && a.subjectId === b.subjectId}
                              renderInput={(p) => <TextField {...p} label="Группа и предмет"/>}/>
                <TextField size="small" type="date" label="Пары до даты" value={upTo} onChange={(e) => { setUpTo(e.target.value); setPreview(null); }} InputLabelProps={{shrink: true}}/>
                <Button variant="contained" disabled={!selected || loading} onClick={run}>Показать</Button>
            </Stack>
            {options.length === 0 && !error && <Alert severity="info">Нет предметов, где вы преподаватель в журнале. Откройте вкладку «Связка» и обновите данные журнала.</Alert>}
            {error && <Alert severity="error">{error}</Alert>}
            {result && <Alert severity={result.severity} onClose={() => setResult(null)}>{result.text}</Alert>}
            {loading && <Box sx={{textAlign: 'center', py: 4}}><CircularProgress/></Box>}
            {preview && !loading && (
                <>
                    <Stack direction="row" spacing={1} sx={{flexWrap: 'wrap', rowGap: 1}}>
                        <Chip label={`пар к расстановке: ${preview.assignments.length}`} color={preview.assignments.length ? 'primary' : 'default'}/>
                        <Chip variant="outlined" label={`в программе тем: ${preview.programTopics}, осталось: ${preview.programLeft}`}/>
                        {preview.alreadyHave > 0 && <Chip variant="outlined" label={`пар уже с темами: ${preview.alreadyHave}`}/>}
                        {preview.noRecord > 0 && <Chip variant="outlined" color="warning" label={`слотов без связи с расписанием: ${preview.noRecord}`}/>}
                        {preview.future > 0 && <Chip variant="outlined" label={`пар позже ${fmtDate(preview.upTo)}: ${preview.future}`}/>}
                        {preview.lessonsWithoutTopic > 0 && <Chip variant="outlined" color="warning" label={`пар без темы (программа закончилась): ${preview.lessonsWithoutTopic}`}/>}
                    </Stack>
                    {!preview.editable && <Alert severity="warning">Этот предмет в журнале вам недоступен для редактирования.</Alert>}
                    {!preview.programApproved && <Alert severity="warning">Рабочая программа в журнале не утверждена — расставлять темы нельзя.</Alert>}
                    {preview.outOfOrder && <Alert severity="warning">В журнале темы стоят не по порядку программы — пары с нарушением выделены красным в списке ниже. Автоматическая расстановка выключена, пока вы не поправите их вручную.</Alert>}
                    {preview.outOfOrder && selected && <FixOrder selected={selected} upTo={upTo} preview={preview} onDone={() => { run(); loadSummary(); }}/>}
                    {!preview.own && <Alert severity="warning">Этот предмет в журнале ведёт {preview.teacher} — темы в него не пишутся, доступен только просмотр.</Alert>}
                    {!preview.writeEnabled && <Alert severity="info">Запись в журнал выключена на сервере (ASURSO_WRITE_ENABLED) — доступен только предпросмотр.</Alert>}
                    {preview.noRecord > 0 && <Alert severity="info">Слоты без связи с расписанием пропущены: их можно создать только вручную в журнале.</Alert>}
                    {preview.assignments.length === 0 && <Alert severity="success">Расставлять нечего: у всех проведённых пар до выбранной даты темы уже стоят.</Alert>}
                    {preview.lessons.length > 0 && (
                        <>
                            <FormControlLabel control={<Switch checked={showHave} onChange={(e) => setShowHave(e.target.checked)}/>} label="Показывать пары, где темы уже стоят"/>
                            <Paper variant="outlined">
                                {preview.lessons.filter((a) => showHave || a.status !== 'HAVE').map((a, i) => (
                                    <Box key={a.lessonId || `s${a.scheduleLessonId}`} sx={{px: 2, py: 1, borderTop: i ? 1 : 0, borderColor: 'divider', opacity: (a.status === 'HAVE' && !a.orderIssue) || a.status === 'FUTURE' ? 0.72 : 1,
                                                                                          borderLeft: '3px solid', borderLeftColor: a.orderIssue ? 'error.main' : a.status === 'ASSIGN' ? 'primary.main' : a.status === 'HAVE' ? 'success.main' : 'divider',
                                                                                          bgcolor: a.orderIssue ? 'rgba(244,67,54,0.10)' : undefined}}>
                                        <Stack direction="row" spacing={1} alignItems="center" sx={{flexWrap: 'wrap', rowGap: 0.5}}>
                                            <Typography sx={{fontWeight: 600, minWidth: 130}}>{fmtDate(a.date)}</Typography>
                                            <Typography variant="body2" color="text.secondary">{a.start || ''} · {typeLabel(a.lessonType)} · {a.duration} ч</Typography>
                                            {a.status === 'ASSIGN' && <Chip size="small" color="primary" label="темы встанут"/>}
                                            {a.status === 'HAVE' && <Chip size="small" color="success" variant="outlined" label="темы уже стоят"/>}
                                            {a.status === 'FUTURE' && <Chip size="small" variant="outlined" label={`позже ${fmtDate(preview.upTo)}`}/>}
                                            {a.status === 'NO_TOPIC' && <Chip size="small" color="warning" variant="outlined" label="тем в программе не осталось"/>}
                                            {a.status === 'NO_SLOT' && <Chip size="small" color="warning" variant="outlined" label="нет слота расписания"/>}
                                            {a.status === 'ASSIGN' && a.create && <Chip size="small" variant="outlined" label="пары в журнале нет — будет создана"/>}
                                            {a.typeMismatch && <Chip size="small" color="warning" variant="outlined" label="тип пары и темы не совпал"/>}
                                            {a.status === 'ASSIGN' && !a.complete && <Chip size="small" color="warning" variant="outlined" label="тем не хватает на всю пару"/>}
                                            {a.orderIssue && <Chip size="small" color="error" label="нарушен порядок"/>}
                                        </Stack>
                                        {a.orderIssue && <Typography variant="body2" color="error.main" sx={{pl: 1, fontWeight: 600}}>⚠ {a.orderIssue}</Typography>}
                                        {a.parts.map((p) => (
                                            <Typography key={p.plannedId} variant="body2" sx={{pl: 1}}>
                                                • {p.name} <Typography component="span" variant="caption" color="text.secondary">({typeLabel(p.type)}, {p.hours} ч)</Typography>
                                            </Typography>
                                        ))}
                                    </Box>
                                ))}
                            </Paper>
                            {preview.assignments.length > 0 && (
                            <Stack direction="row" spacing={1} sx={{flexWrap: 'wrap', rowGap: 1}}>
                                    <Button variant="outlined" disabled={!canApply || busy}
                                            onClick={() => apply(1, `Расставить темы только на первую пару (${fmtDate(preview.assignments[0].date)})? Это проба: потом проверьте её в журнале.`)}>
                                        Расставить только первую пару (проба)
                                    </Button>
                                    <Button variant="contained" disabled={!canApply || busy}
                                            onClick={() => apply(null, `Расставить темы на ${preview.assignments.length} пар(ы) в журнале АСУ РСО? Изменятся только темы пар, где их ещё нет.`)}>
                                        Расставить все ({preview.assignments.length})
                                    </Button>
                                    {busy && <CircularProgress size={22}/>}
                                </Stack>
                            )}
                        </>
                    )}
                </>
            )}
        </Stack>
    );
};

const AdminAsursoPage = () => {
    const [params] = useSearchParams();
    const [tab, setTab] = useState(params.get('tab') || 'links');
    const [status, setStatus] = useState(null);

    useEffect(() => {
        document.title = 'АСУ РСО';
        ApiService.getAsursoStatus().then(setStatus).catch(() => setStatus({configured: false}));
    }, []);

    return (
        <Container maxWidth="md" sx={{py: 4}}>
            <Typography variant="h4" sx={{mb: 2}}>АСУ РСО</Typography>
            {status && !status.configured && (
                <Alert severity="warning" sx={{mb: 2}}>Доступ к журналу не настроен: на сервере не заданы ASURSO_LOGIN и ASURSO_PASSWORD.</Alert>
            )}
            <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{mb: 2}}>
                <Tab value="links" label="Связка"/>
                <Tab value="compare" label="Сверка журнала"/>
                <Tab value="attendance" label="Посещаемость"/>
                <Tab value="topics" label="Темы"/>
                <Tab value="grades" label="Оценки"/>
            </Tabs>
            {status?.configured && (tab === 'links' ? <LinksTab/> : tab === 'compare' ? <CompareTab/> : tab === 'topics' ? <TopicsTab/> : tab === 'grades' ? <GradesTab/> : <AttendanceTab/>)}
        </Container>
    );
};

export default AdminAsursoPage;
